import React from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { SideBarAdUnitContainer }
from '@fuww/library/src/Advertisement';
import { GridCell, Grid } from '@fuww/library/src/Grid';
import { NewsListDivider }
from '@fuww/library/src/List/NewsList';
import dynamic from 'next/dynamic';
import breakpoints from '@fuww/library/src/Theme/breakpoints';
import HiddenLoader from '@fuww/library/src/HiddenLoader';
import withQuery from '../../../lib/withQuery';
import jobsQuery from '../../../lib/queries/jobs/jobsQuery.graphql';
import relatedProductsQuery
from '../../../lib/queries/products/relatedProductsQuery.graphql';
import relatedProfilesCount
from '../../../lib/queries/news/relatedProfilesCount.graphql';
import AdUnit from '../../../lib/adunit';
import transformKeywords from '../../../lib/transformKeywords';
import { useSiteContext } from '../../SiteContext';
import InView from '../../InView';
import loading from '../../../lib/loading';
import getSourceSet from '../../../lib/getSourceSet';
import { latestJobsImageUrls } from '../../Jobs/LatestJobs';
import {
  articlePageHighlightedJobsImageConfigurations,
  relatedProductsImageConfigurations,
} from '../../../lib/imageConfigurations';
import messages from '../../../lib/messages.mjs';

const hasFashionFinderProducts = (locale) => ([
  'nl-NL',
  'de-DE',
  'en-US',
  'en-GB',
].includes(locale));

const MoreNews = dynamic(
  () => import('../NewsList/Categories/MoreNews'),
  {
    loading,
    ssr: false,
  },
);

const MostRead = dynamic(
  () => import('../NewsList/Categories/MostRead'),
  {
    loading,
    ssr: false,
  },
);

const LatestJobs = dynamic(
  () => import('../../Jobs/LatestJobs'),
  {
    loading,
    ssr: false,
  },
);

const HighlightedJobs = dynamic(
  () => import('../../Jobs/HighlightedJobs'),
  {
    loading,
    ssr: false,
  },
);

const RelatedProducts = dynamic(
  () => import('../../Products/RelatedProducts'),
  {
    loading,
    ssr: false,
  },
);

const getUpperProductsBlock = (
  keywords,
  hasFashionFinder,
  hasRelatedPremiumProfiles,
  MarketplaceBlock,
  FashionFinderBlock,
) => {
  if (!keywords) return null;
  if (hasRelatedPremiumProfiles) return MarketplaceBlock;
  return hasFashionFinder ? FashionFinderBlock : null;
};

const getLowerProductsBlock = (
  keywords,
  hasFashionFinder,
  hasRelatedPremiumProfiles,
  MarketplaceBlock,
  FashionFinderBlock,
) => {
  if (!keywords) return null;
  if (!hasRelatedPremiumProfiles) return MarketplaceBlock;
  return hasFashionFinder ? FashionFinderBlock : null;
};

const SideBar = ({
  keywords,
  articleId,
  data,
}) => {
  const {
    newsboard,
    jobsLocale,
    locale,
  } = useSiteContext();

  const intl = useIntl();

  const hasRelatedPremiumProfiles = data?.profilesCount > 0;
  const relatedProductsErrorMessage = 'Error loading related products...';
  const jobErrorMessage = 'Error loading job of the week...';
  const latestJobsErrorMessage = 'Error loading latest jobs...';
  const transformedKeywords = transformKeywords(keywords);
  const highlightedJobsVariables = {
    locales: jobsLocale,
    highlightLocale: jobsLocale,
    highlightType: 'JOB_OF_THE_WEEK',
    offset: 0,
    limit: 5,
    imageConfigurations: articlePageHighlightedJobsImageConfigurations,
  };
  const relatedProductsVariables = {
    maybeBrandKeywords: transformedKeywords,
    maybeKeywords: transformedKeywords,
    locales: 'en-US',
    limit: 6,
    offset: 0,
    sort: 'RANDOM',
    marketId: 1,
    imageConfigurations: relatedProductsImageConfigurations,
  };

  const { sidebar: imageUrls } = latestJobsImageUrls;

  const sourceSet = getSourceSet(
    highlightedJobsVariables.imageConfigurations, imageUrls,
  );

  const FashionFinderBlock = (
    <GridCell
      sm={6}
      lg={12}
      minHeight="805px"
      padding="0 16px 0"
    >
      <InView>
        <RelatedProducts
          locales={locale}
          articleId={articleId}
          query={relatedProductsQuery}
          variables={{
            ...relatedProductsVariables,
            marketId: 8,
            locales: locale,
          }}
          errorMessage={relatedProductsErrorMessage}
          fetchPolicy="no-cache"
          headerText={intl.formatMessage(messages['fashion-finder.title'])}
          callToActionMessage={intl.formatMessage(messages.buyNow)}
          isFashionFinder
        />
      </InView>
    </GridCell>
  );

  const MarketplaceBlock = (
    <GridCell
      sm={6}
      lg={12}
      minHeight="805px"
      padding="0 16px 0"
    >
      <InView>
        <RelatedProducts
          locales={locale}
          articleId={articleId}
          query={relatedProductsQuery}
          variables={relatedProductsVariables}
          errorMessage={relatedProductsErrorMessage}
          fetchPolicy="no-cache"
          headerText={intl.formatMessage(messages.marketplace)}
        />
      </InView>
    </GridCell>
  );

  return (
    <Grid>
      {
        getUpperProductsBlock(
          keywords,
          hasFashionFinderProducts(locale),
          hasRelatedPremiumProfiles,
          MarketplaceBlock,
          FashionFinderBlock,
        )
      }
      <GridCell
        sm={6}
        lg={12}
        padding="0 16px 24px"
        minHeight="656px"
      >
        <SideBarAdUnitContainer>
          <AdUnit
            adUnit="article_halfpage"
            sizes={[[300, 600]]}
            minHeight="600px"
          />
        </SideBarAdUnitContainer>
      </GridCell>
      {
        getLowerProductsBlock(
          keywords,
          hasFashionFinderProducts(locale),
          hasRelatedPremiumProfiles,
          MarketplaceBlock,
          FashionFinderBlock,
        )
      }
      <GridCell sm={6} lg={12} padding="0 16px 24px">
        <InView>
          <HighlightedJobs
            query={jobsQuery}
            variables={highlightedJobsVariables}
            errorMessage={jobErrorMessage}
            loader={<HiddenLoader />}
          />
        </InView>
      </GridCell>
      <GridCell sm={6} lg={12} padding="0 16px 24px">
        <InView>
          <MostRead
            showImage
            showCategory
          />
        </InView>
      </GridCell>
      <GridCell sm={6} lg={12} padding="0 16px 24px">
        <SideBarAdUnitContainer>
          <AdUnit
            adUnit="article_rectangle"
            sizes={[[300, 250]]}
            minHeight="250px"
          />
        </SideBarAdUnitContainer>
      </GridCell>
      <GridCell sm={6} lg={12} padding="0 16px 24px">
        <InView>
          <MoreNews
            showCategory
            showImage
            categoryHeadline
            variables={{
              withoutSummaryAndLabel: true,
              withoutDescriptionAndCreator: true,
            }}
          />
          <NewsListDivider />
        </InView>
      </GridCell>
      {newsboard !== 'FASHIONUNITED_INFO' && (
        <GridCell sm={6} lg={12} padding="0 16px 24px">
          <InView>
            <LatestJobs
              query={jobsQuery}
              variables={{
                locales: jobsLocale,
                offset: 0,
                limit: 5,
              }}
              errorMessage={latestJobsErrorMessage}
              placeholder={imageUrls[0]}
              src={imageUrls[7]}
              srcSet={sourceSet}
              sizes={`
                (min-width: ${breakpoints.lg}px) 311px,
                (min-width: ${breakpoints.md}px) 460px,
                (min-width: ${breakpoints.sm}px) 372px,
                568px
              `}
            />
          </InView>
        </GridCell>
      )}
    </Grid>
  );
};

SideBar.propTypes = {
  keywords: PropTypes.string,
  articleId: PropTypes.number,
  data: PropTypes.shape({
    profilesCount: PropTypes.number,
  }),
};

SideBar.defaultProps = {
  keywords: undefined,
  articleId: undefined,
  data: undefined,
};

const SideBarWithQuery = withQuery(SideBar, { renderAlways: true });

const SideBarWithProfilesCount = ({ keywords, ...properties }) => (
  <SideBarWithQuery
    query={relatedProfilesCount}
    variables={{
      newsTags: keywords?.split(','),
    }}
    errorMessage="Error loading related profiles"
    keywords={keywords}
    loader={<HiddenLoader />}
    {...properties}
  />
);

SideBarWithProfilesCount.propTypes = {
  keywords: PropTypes.string,
};

SideBarWithProfilesCount.defaultProps = {
  keywords: undefined,
};

export default SideBarWithProfilesCount;
